import {
    ErrorResponse,
    ResponseArray,
    ResponseObject,
} from '@vnetwork/datacentral-core/types'
import { datacentral } from './http'
import {
    CreateRecentObjectParams,
    CreateStarredObjectParams,
    Metadata,
    RenameObjectParams,
    SharedLinkResponse,
    ShareLinkParams,
    StarredObjectsResponse,
    ThumbnailResponse,
    GetObjectPermissionLevelRow,
    EditShareRecipientParams,
    ObjectType,
    EmployeeNamesResponse,
    DownloadMultipleFilesParams,
} from '../types'
import { AxiosError } from 'axios'
import { setFolderName } from '../utils'

interface CommonPrefix {
    Prefix: string
    FolderName?: string
}

interface Content {
    Key: string
    LastModified: string
    Size: number
}

export interface ListObjectResponse {
    folders: CommonPrefix[]
    files: Content[]
}

export interface ListObjectWithPaginationResponse extends ListObjectResponse {
    pageSize: number
    currentPage: number
    hasMore: boolean
    nextContinuationToken: string | null
}

export const ListObject = async (
    path: string | undefined
): Promise<ResponseObject<ListObjectResponse>> => {
    try {
        const response = await datacentral().get(`/drive`, {
            params: { path },
        })
        const folders = response.data.result?.items?.CommonPrefixes || []
        const files =
            response.data.result?.items?.Contents?.filter(
                (file: Content) => file.Size > 0
            ) || []

        return {
            message: response.data.message,
            result: {
                folders: folders,
                files: files,
            },
        }
    } catch (error: any) {
        throw new Error(
            error.response?.data?.error_description ||
                error.message ||
                'Failed to fetch objects'
        )
    }
}

export const ListObjectWithPaginationV2 = async (
    path: string | undefined,
    continuationToken: string,
    search?: string
): Promise<ListObjectWithPaginationResponse> => {
    try {
        const response = await datacentral().get(`/drive/v2`, {
            params: {
                path,
                continuation_token: continuationToken,
                query: search ?? '',
            },
        })

        // Support both camelCase and PascalCase from backend
        const result = response.data.result || {}
        const items = result.items || result.Items || {}
        const folders: CommonPrefix[] = items.CommonPrefixes || []
        const files: Content[] = (items.Contents || []).filter(
            (file: Content) => file.Size > 0
        )

        folders.forEach((folder: CommonPrefix) => {
            if (folder.FolderName) {
                setFolderName(folder.Prefix, folder.FolderName)
            }
        })

        return {
            pageSize: result.pageSize,
            currentPage: result.current_page,
            hasMore: result.has_more,
            folders,
            files,
            nextContinuationToken: result.nextContinuationToken,
        }
    } catch (error: any) {
        throw new Error(
            error.response?.data?.error_description ||
                error.message ||
                'Failed to fetch objects with pagination'
        )
    }
}

export const ListRootDrive = async (): Promise<
    ResponseObject<ListObjectResponse>
> => {
    try {
        const response = await datacentral().get(`/admin/drive`)
        const folders = response.data.result.items.CommonPrefixes || []
        const files =
            response.data.result.items.Contents?.filter(
                (file: Content) => file.Size > 0
            ) || []

        return {
            message: response.data.message,
            result: {
                folders: folders,
                files: files,
            },
        }
    } catch (error: any) {
        throw new Error(
            error.response?.data?.error_description ||
                error.message ||
                'Failed to fetch root drive objects'
        )
    }
}

export const CreateFolder = async (
    prefix: string | undefined,
    key: string
): Promise<ResponseObject<void>> => {
    if (!prefix) throw new AxiosError<ErrorResponse>()
    try {
        // Ensure prefix starts with 'drives/' and has no duplicate slashes
        if (
            !prefix.startsWith('drives/') &&
            !prefix.startsWith('pub/drives/')
        ) {
            prefix = 'drives/' + prefix.replace(/^\/+/, '')
        }
        // Remove any accidental double slashes
        prefix = prefix.replace(/\/{2,}/g, '/')
        const response = await datacentral().post('/drive/folders', {
            prefix,
            key,
        })
        return response.data
    } catch (error: any) {
        throw error
    }
}

export const RenameObject = async (
    payload: RenameObjectParams
): Promise<ResponseObject<void>> => {
    try {
        const response = await datacentral().put(`/drive`, payload)
        return response.data
    } catch (error: any) {
        throw error
    }
}

// A presigned download URL for a Drive object. The app keeps its own copy of this
// call (src/services/drive-download.ts) for the shared file preview, which reads
// it and may not import a feature module (#33); this is the package's.
export const PresignedGetUrl = async (
    path: string
): Promise<ResponseObject<string>> => {
    const response = await datacentral().get('/drive/download', {
        params: { path: path },
    })
    return response.data
}

export const DeleteFile = async (
    path: string
): Promise<ResponseObject<void>> => {
    try {
        const response = await datacentral().delete('/drive', {
            params: { path: path },
        })
        return response.data
    } catch (error: any) {
        throw error
    }
}

export const ListStarredObjects = async (
    uuid: string | undefined
): Promise<ResponseArray<StarredObjectsResponse>> => {
    if (!uuid) return Promise.reject(new Error('UUID is required'))

    try {
        const response = await datacentral().get(`/drive/starred`, {
            params: { uuid: uuid },
        })
        return response.data
    } catch (error: any) {
        throw new Error(
            error.response?.data?.error_description ||
                error.message ||
                'Failed to fetch starred objects'
        )
    }
}

export const ListStarredObjectKeys = async (
    uuid: string | undefined
): Promise<ResponseArray<string>> => {
    if (!uuid) return Promise.reject(new Error('UUID is required'))
    try {
        const response = await datacentral().get(`/drive/starred/keys`, {
            params: { uuid: uuid },
        })
        return response.data
    } catch (error: any) {
        throw new Error(
            error.response?.data?.message || 'Failed to fetch starred objects'
        )
    }
}

export const AddObjectToStarred = async (
    payload: CreateStarredObjectParams
) => {
    try {
        await datacentral().post('/drive/starred', payload)
    } catch (error: any) {
        throw new Error(
            error.response?.data?.message || 'Failed to add object to starred'
        )
    }
}

export const RemoveObjectFromStarred = async (
    uuid: string,
    objectName: string
) => {
    try {
        const response = await datacentral().put('/drive/starred/remove', {
            starred_object_key: objectName,
            employee_uuid: uuid,
        })
        return response
    } catch (error: any) {
        throw error
    }
}

export const AddObjectToRecent = async (payload: CreateRecentObjectParams) => {
    try {
        const response = await datacentral().post('drive/recent', payload)
        return response
    } catch (error: any) {
        throw error
    }
}

export const DuplicateObject = async (object_key: string) => {
    try {
        const response = await datacentral().put('drive/duplicate', {
            object_key,
        })
        return response.data
    } catch (error: any) {
        throw error
    }
}

export const FetchThumbnail = async (
    name: string
): Promise<ResponseObject<ThumbnailResponse>> => {
    try {
        const response = await datacentral().get('/drive/thumbnail', {
            params: { path: name },
        })
        return response.data
    } catch (error: any) {
        throw error
    }
}

/**
 * Download a folder as a zip file from the backend.
 * @param folderPath The S3 folder path to download (should end with a slash, but the backend will handle it if not).
 * @returns A Blob representing the zip file.
 */
export const DownloadFolder = async (
    folderPath: string,
    signal?: AbortSignal
): Promise<Blob> => {
    try {
        const response = await datacentral().get('/drive/download-folder', {
            params: { path: folderPath },
            responseType: 'blob',
            signal,
        })
        return response.data as Blob
    } catch (error: any) {
        if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
            throw new Error('Download cancelled')
        }
        throw new Error(
            error.response?.data?.message ||
                error.response?.data?.errorMessage ||
                'Failed to download folder'
        )
    }
}

export const DownloadMultipleFiles = async (
    payload: DownloadMultipleFilesParams,
    signal?: AbortSignal
): Promise<Blob> => {
    try {
        const response = await datacentral().post(
            '/drive/download-multiple',
            payload,
            {
                responseType: 'blob',
                signal,
            }
        )
        return response.data as Blob
    } catch (error: any) {
        if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
            throw new Error('Download cancelled')
        }

        throw new Error(
            error.response?.data?.message ||
                error.response?.data?.errorMessage ||
                'Failed to download folder'
        )
    }
}

export const CreateShareLink = async (
    payload: ShareLinkParams
): Promise<ResponseObject<string>> => {
    const response = await datacentral().post('/drive/share', payload)
    return response.data
}

export const GetSharedLinkV2 = async (
    token: string
): Promise<ResponseObject<SharedLinkResponse>> => {
    const response = await datacentral().get('/drive/share/v2', {
        params: { token: token },
    })
    return response.data
}

export const EditShareRecipient = async (
    payload: EditShareRecipientParams[]
): Promise<ResponseObject<void>> => {
    const response = await datacentral().put(
        '/drive/share/recipient-list',
        payload
    )
    return response.data
}

export const GetPublicShareLinks = async (
    objectType: ObjectType,
    objectKey: string
): Promise<ResponseArray<SharedLinkResponse>> => {
    const response = await datacentral().post('/drive/share/public-links', {
        share_object_type: objectType,
        share_object_key: objectKey,
    })
    return response.data
}

export const RevokeShareLink = async (shareLinkId: number) => {
    const response = await datacentral().post('/drive/share/revoke', {
        share_link_id: shareLinkId,
    })
    return response.data
}

export const ListSharedWithMe = async (
    employee_id: number
): Promise<ResponseArray<SharedLinkResponse>> => {
    const response = await datacentral().get(
        `/drive/shared-with-me/${employee_id}`
    )
    return response.data
}

export const GetObjectMetadata = async (
    path: string
): Promise<ResponseObject<Metadata>> => {
    const response = await datacentral().get('/drive/file', {
        params: { path: path },
    })
    return response.data
}

export const GetObjectPermissionLevel = async (
    object_key: string,
    recipientID?: number,
    objectType?: ObjectType
): Promise<ResponseObject<GetObjectPermissionLevelRow>> => {
    try {
        const response = await datacentral().get('/drive/share/permission', {
            params: {
                object_key,
                recipientID,
                objectType,
            },
        })
        return response.data
    } catch (error: any) {
        throw error
    }
}

export const CheckModifyItem = async (
    object_key: string
): Promise<ResponseObject<boolean>> => {
    try {
        const response = await datacentral().get('/drive/modify/check', {
            params: { object_key },
        })
        return response.data
    } catch (error: any) {
        throw error
    }
}

export const CheckUploadItem = async (
    prefix: string
): Promise<ResponseObject<boolean>> => {
    try {
        const response = await datacentral().get('/drive/upload/check', {
            params: { prefix: prefix },
        })
        return response.data
    } catch (error: any) {
        throw error
    }
}

export const GetEmployeeNameByUuid = async (
    uuid: string
): Promise<ResponseObject<string>> => {
    const response = await datacentral().get(`/employees/names`, {
        params: { uuid: uuid },
    })
    return response.data
}

// Changed to send uuids as payload (POST request)
export const GetEmployeeNamesByUuid = async (
    uuids: string[]
): Promise<ResponseObject<EmployeeNamesResponse[]>> => {
    const response = await datacentral().post(`/employees/names/bulk`, {
        uuids,
    })
    return response.data
}
